/**
 * ProgressDashboard - Main progress tracking dashboard
 *
 * Features:
 * - "By Student" tab - StudentProgress component
 * - "By Outcome" tab - OutcomeProgress component
 * - Class selector dropdown
 * - Date range filter (future enhancement)
 */

import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Users,
  Target,
  AlertCircle,
  TrendingUp,
  RefreshCw,
  BarChart3,
  Database,
} from "lucide-react";
import { StudentProgress, type MergedStudentProgress } from "./StudentProgress";
import { OutcomeProgress } from "./OutcomeProgress";
import {
  progressService,
  isRequestCancelled,
  type StudentProgress as StudentProgressType,
  type OutcomeProgress as OutcomeProgressType,
  type StudentDetail,
  type OutcomeDetail,
} from "@/services/progress.service";
import { classService, type ServerClass } from "@/services/class.service";
import { useLocalDbStore } from "@/store/localDbStore";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

// =============================================================================
// Types
// =============================================================================

export interface ProgressDashboardProps {
  /** Additional class names */
  className?: string;
}

// =============================================================================
// Stats Cards Component
// =============================================================================

interface StatsCardsProps {
  studentCount: number;
  outcomeCount: number;
  avgMastery: number | null;
  loading: boolean;
}

function StatsCards({ studentCount, outcomeCount, avgMastery, loading }: StatsCardsProps) {
  if (loading) {
    return (
      <div className="grid gap-4 grid-cols-2 lg:grid-cols-3">
        {[1, 2, 3].map((i) => (
          <Card key={i}>
            <CardHeader className="pb-2">
              <Skeleton className="h-4 w-20" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-8 w-12" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="grid gap-4 grid-cols-2 lg:grid-cols-3">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Users className="h-4 w-4 text-muted-foreground" />
            Students
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{studentCount}</div>
          <p className="text-xs text-muted-foreground">with assessments</p>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Target className="h-4 w-4 text-muted-foreground" />
            Outcomes
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{outcomeCount}</div>
          <p className="text-xs text-muted-foreground">assessed</p>
        </CardContent>
      </Card>
      <Card className="col-span-2 lg:col-span-1">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
            Class Average
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className={cn(
            'text-2xl font-bold',
            avgMastery !== null && avgMastery >= 80 && 'text-green-600',
            avgMastery !== null && avgMastery >= 50 && avgMastery < 80 && 'text-yellow-600',
            avgMastery !== null && avgMastery < 50 && 'text-red-600',
          )}>
            {avgMastery !== null ? `${Math.round(avgMastery)}%` : '--'}
          </div>
          <p className="text-xs text-muted-foreground">mastery rate</p>
        </CardContent>
      </Card>
    </div>
  );
}

// =============================================================================
// Main Component
// =============================================================================

export function ProgressDashboard({ className }: ProgressDashboardProps) {
  // Tab state
  const [activeTab, setActiveTab] = useState<'students' | 'outcomes'>('students');

  // Class selection
  const [serverClasses, setServerClasses] = useState<ServerClass[]>([]);
  const [selectedClassId, setSelectedClassId] = useState<string | null>(null);
  const [classesLoading, setClassesLoading] = useState(true);

  // Student progress state
  const [studentProgress, setStudentProgress] = useState<StudentProgressType[]>([]);
  const [studentLoading, setStudentLoading] = useState(false);
  const [studentError, setStudentError] = useState<string | null>(null);

  // Outcome progress state
  const [outcomeProgress, setOutcomeProgress] = useState<OutcomeProgressType[]>([]);
  const [outcomeLoading, setOutcomeLoading] = useState(false);
  const [outcomeError, setOutcomeError] = useState<string | null>(null);

  // Detail panel state
  const [expandedStudentSheet, setExpandedStudentSheet] = useState<string | null>(null);
  const [studentDetail, setStudentDetail] = useState<StudentDetail | null>(null);
  const [studentDetailLoading, setStudentDetailLoading] = useState(false);

  const [expandedOutcomeId, setExpandedOutcomeId] = useState<string | null>(null);
  const [outcomeDetail, setOutcomeDetail] = useState<OutcomeDetail | null>(null);
  const [outcomeDetailLoading, setOutcomeDetailLoading] = useState(false);

  // Local DB state for student name lookups
  const isLocalDbConnected = useLocalDbStore((s) => s.isConnected);
  const localClasses = useLocalDbStore((s) => s.classes);
  const getStudentBySheetNumber = useLocalDbStore((s) => s.getStudentBySheetNumber);
  const selectLocalClass = useLocalDbStore((s) => s.selectClass);

  // Abort controller refs
  const abortRef = useRef<AbortController | null>(null);

  // Fetch server classes on mount
  useEffect(() => {
    const controller = new AbortController();

    const fetchClasses = async () => {
      try {
        setClassesLoading(true);
        const classes = await classService.getClasses(false, controller.signal);
        if (!controller.signal.aborted) {
          setServerClasses(classes);
          // Auto-select first class if available
          if (classes.length > 0 && !selectedClassId) {
            setSelectedClassId(classes[0].classId);
          }
        }
      } catch (err) {
        if (!isRequestCancelled(err)) {
          console.error('Failed to load classes:', err);
        }
      } finally {
        if (!controller.signal.aborted) {
          setClassesLoading(false);
        }
      }
    };

    fetchClasses();
    return () => controller.abort();
  }, []);

  // Sync local class selection when server class changes
  useEffect(() => {
    if (!isLocalDbConnected || !selectedClassId) {
      selectLocalClass(null);
      return;
    }
    const matchingLocalClass = localClasses.find(
      (lc) => lc.serverClassId === selectedClassId
    );
    if (matchingLocalClass) {
      selectLocalClass(matchingLocalClass.id);
    } else {
      selectLocalClass(null);
    }
  }, [selectedClassId, localClasses, isLocalDbConnected, selectLocalClass]);

  // Fetch student progress when class changes
  useEffect(() => {
    if (!selectedClassId) {
      setStudentProgress([]);
      return;
    }

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    const fetchStudentProgress = async () => {
      try {
        setStudentLoading(true);
        setStudentError(null);
        const result = await progressService.getClassStudentProgress(
          selectedClassId,
          controller.signal
        );
        if (!controller.signal.aborted) {
          setStudentProgress(result.items);
        }
      } catch (err) {
        if (!isRequestCancelled(err)) {
          const message = err instanceof Error ? err.message : 'Failed to load student progress';
          setStudentError(message);
        }
      } finally {
        if (!controller.signal.aborted) {
          setStudentLoading(false);
        }
      }
    };

    fetchStudentProgress();
    return () => controller.abort();
  }, [selectedClassId]);

  // Fetch outcome progress when class changes
  useEffect(() => {
    if (!selectedClassId) {
      setOutcomeProgress([]);
      return;
    }

    const controller = new AbortController();

    const fetchOutcomeProgress = async () => {
      try {
        setOutcomeLoading(true);
        setOutcomeError(null);
        const result = await progressService.getClassOutcomeProgress(
          selectedClassId,
          controller.signal
        );
        if (!controller.signal.aborted) {
          setOutcomeProgress(result.items);
        }
      } catch (err) {
        if (!isRequestCancelled(err)) {
          const message = err instanceof Error ? err.message : 'Failed to load outcome progress';
          setOutcomeError(message);
        }
      } finally {
        if (!controller.signal.aborted) {
          setOutcomeLoading(false);
        }
      }
    };

    fetchOutcomeProgress();
    return () => controller.abort();
  }, [selectedClassId]);

  // Fetch student detail when expanded
  useEffect(() => {
    if (!expandedStudentSheet || !selectedClassId) {
      setStudentDetail(null);
      return;
    }

    const controller = new AbortController();

    const fetchStudentDetail = async () => {
      try {
        setStudentDetailLoading(true);
        const detail = await progressService.getStudentDetail(
          expandedStudentSheet,
          selectedClassId,
          controller.signal
        );
        if (!controller.signal.aborted) {
          setStudentDetail(detail);
        }
      } catch (err) {
        if (!isRequestCancelled(err)) {
          toast.error('Failed to load student details');
        }
      } finally {
        if (!controller.signal.aborted) {
          setStudentDetailLoading(false);
        }
      }
    };

    fetchStudentDetail();
    return () => controller.abort();
  }, [expandedStudentSheet, selectedClassId]);

  // Fetch outcome detail when expanded
  useEffect(() => {
    if (!expandedOutcomeId || !selectedClassId) {
      setOutcomeDetail(null);
      return;
    }

    const controller = new AbortController();

    const fetchOutcomeDetail = async () => {
      try {
        setOutcomeDetailLoading(true);
        const detail = await progressService.getOutcomeDetail(
          expandedOutcomeId,
          selectedClassId,
          controller.signal
        );
        if (!controller.signal.aborted) {
          setOutcomeDetail(detail);
        }
      } catch (err) {
        if (!isRequestCancelled(err)) {
          toast.error('Failed to load outcome details');
        }
      } finally {
        if (!controller.signal.aborted) {
          setOutcomeDetailLoading(false);
        }
      }
    };

    fetchOutcomeDetail();
    return () => controller.abort();
  }, [expandedOutcomeId, selectedClassId]);

  // Merge student progress with local names
  const mergedStudentProgress: MergedStudentProgress[] = useMemo(() => {
    return studentProgress.map((sp) => {
      const localStudent = isLocalDbConnected ? getStudentBySheetNumber(sp.sheetNumber) : null;
      return {
        ...sp,
        studentName: localStudent?.name ?? `Sheet #${sp.sheetNumber}`,
        studentId: localStudent?.id ?? null,
        isUnknown: !localStudent,
      };
    });
  }, [studentProgress, isLocalDbConnected, getStudentBySheetNumber]);

  // Student name lookup function for outcome detail
  const getStudentName = useCallback(
    (sheetNumber: string) => {
      const localStudent = isLocalDbConnected ? getStudentBySheetNumber(sheetNumber) : null;
      return {
        name: localStudent?.name ?? `Sheet #${sheetNumber}`,
        isUnknown: !localStudent,
      };
    },
    [isLocalDbConnected, getStudentBySheetNumber]
  );

  // Calculate stats
  const stats = useMemo(() => {
    const studentCount = studentProgress.length;
    const outcomeCount = outcomeProgress.length;

    // Calculate average mastery from student data
    let avgMastery: number | null = null;
    if (studentProgress.length > 0) {
      const total = studentProgress.reduce((sum, s) => sum + s.avgPercentage, 0);
      avgMastery = total / studentProgress.length;
    }

    return { studentCount, outcomeCount, avgMastery };
  }, [studentProgress, outcomeProgress]);

  // Handlers
  const handleStudentClick = useCallback((sheetNumber: string) => {
    setExpandedStudentSheet((prev) => (prev === sheetNumber ? null : sheetNumber));
  }, []);

  const handleStudentDetailClose = useCallback(() => {
    setExpandedStudentSheet(null);
  }, []);

  const handleOutcomeClick = useCallback((outcomeId: string) => {
    setExpandedOutcomeId((prev) => (prev === outcomeId ? null : outcomeId));
  }, []);

  const handleOutcomeDetailClose = useCallback(() => {
    setExpandedOutcomeId(null);
  }, []);

  const handleRefresh = useCallback(() => {
    // Trigger re-fetch by toggling class selection
    if (selectedClassId) {
      const currentClassId = selectedClassId;
      setSelectedClassId(null);
      setTimeout(() => setSelectedClassId(currentClassId), 0);
    }
  }, [selectedClassId]);

  // Get class display name
  const getClassDisplayName = useCallback(
    (classId: string): string => {
      const localClass = localClasses.find((c) => c.serverClassId === classId);
      if (localClass) {
        return localClass.name + (localClass.grade ? ` (Grade ${localClass.grade})` : '');
      }
      const serverClass = serverClasses.find((c) => c.classId === classId);
      if (serverClass) {
        return serverClass.name + (serverClass.grade ? ` (Grade ${serverClass.grade})` : '');
      }
      return 'Unknown Class';
    },
    [localClasses, serverClasses]
  );

  // No classes state
  if (!classesLoading && serverClasses.length === 0) {
    return (
      <div className={cn('space-y-6', className)}>
        <Card className="p-8 text-center">
          <Users className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
          <CardTitle className="mb-2">No Classes Found</CardTitle>
          <CardDescription className="mb-4">
            Create a class and scan some worksheets to see progress data.
          </CardDescription>
          <Button asChild>
            <a href="/students">Create a Class</a>
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className={cn('space-y-6', className)}>
      {/* Header with Class Selector */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          {classesLoading ? (
            <Skeleton className="h-10 w-[200px]" />
          ) : (
            <Select
              value={selectedClassId || ''}
              onValueChange={(v) => setSelectedClassId(v || null)}
            >
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Select a class..." />
              </SelectTrigger>
              <SelectContent>
                {serverClasses.map((cls) => (
                  <SelectItem key={cls.classId} value={cls.classId}>
                    <span className="flex items-center gap-2">
                      <Users className="h-4 w-4" />
                      {getClassDisplayName(cls.classId)}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={handleRefresh}
            disabled={studentLoading || outcomeLoading}
            title="Refresh data"
          >
            <RefreshCw className={cn('h-4 w-4', (studentLoading || outcomeLoading) && 'animate-spin')} />
          </Button>
        </div>
      </div>

      {/* Database Connection Notice */}
      {!isLocalDbConnected && (
        <Alert>
          <Database className="h-4 w-4" />
          <AlertTitle>Connect Student Database</AlertTitle>
          <AlertDescription>
            Open your student database on the{' '}
            <a href="/students" className="font-medium underline underline-offset-4">
              Students page
            </a>{' '}
            to see student names. Without it, only sheet numbers will be shown.
          </AlertDescription>
        </Alert>
      )}

      {/* Error Alerts */}
      {(studentError || outcomeError) && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error loading data</AlertTitle>
          <AlertDescription>{studentError || outcomeError}</AlertDescription>
        </Alert>
      )}

      {/* Stats Cards */}
      <StatsCards
        studentCount={stats.studentCount}
        outcomeCount={stats.outcomeCount}
        avgMastery={stats.avgMastery}
        loading={studentLoading || outcomeLoading}
      />

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'students' | 'outcomes')}>
        <div className="overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0">
          <TabsList className="inline-flex w-auto min-w-full md:w-full md:grid md:grid-cols-2">
            <TabsTrigger value="students" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              By Student
              <Badge variant="secondary" className="ml-1 hidden sm:inline-flex">
                {stats.studentCount}
              </Badge>
            </TabsTrigger>
            <TabsTrigger value="outcomes" className="flex items-center gap-2">
              <Target className="h-4 w-4" />
              By Outcome
              <Badge variant="secondary" className="ml-1 hidden sm:inline-flex">
                {stats.outcomeCount}
              </Badge>
            </TabsTrigger>
          </TabsList>
        </div>

        {/* By Student Tab */}
        <TabsContent value="students" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Users className="h-5 w-5" />
                Student Progress
              </CardTitle>
              <CardDescription>
                Click on a student to view detailed progress breakdown
              </CardDescription>
            </CardHeader>
            <CardContent>
              <StudentProgress
                items={mergedStudentProgress}
                loading={studentLoading}
                expandedStudentDetail={studentDetail}
                detailLoading={studentDetailLoading}
                expandedSheetNumber={expandedStudentSheet}
                onStudentClick={handleStudentClick}
                onDetailClose={handleStudentDetailClose}
              />
            </CardContent>
          </Card>
        </TabsContent>

        {/* By Outcome Tab */}
        <TabsContent value="outcomes" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Target className="h-5 w-5" />
                Outcome Progress
              </CardTitle>
              <CardDescription>
                Grouped by subject - click an outcome to see per-student breakdown
              </CardDescription>
            </CardHeader>
            <CardContent>
              <OutcomeProgress
                items={outcomeProgress}
                loading={outcomeLoading}
                expandedOutcomeDetail={outcomeDetail}
                detailLoading={outcomeDetailLoading}
                expandedOutcomeId={expandedOutcomeId}
                onOutcomeClick={handleOutcomeClick}
                onDetailClose={handleOutcomeDetailClose}
                getStudentName={getStudentName}
              />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default ProgressDashboard;
